import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronRight,
  Award,
  Plus,
  X,
  Trash2,
  Info,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { SKILL_CATEGORIES } from "../data/skills";

const ALL_CATEGORY_KEYS = ["All", ...SKILL_CATEGORIES.map((c) => c.key)];

export default function SkillRoadmap({
  notes,
  belt,
  currentBelt,
  onOpenPromotion,
  onAddCustomSkill,
  onRemoveCustomSkill,
  allSkills,
  completedSkills,
  progress,
}) {
  const [skillFilter, setSkillFilter] = useState("All");
  const [addSkillOpen, setAddSkillOpen] = useState(false);
  const [newSkill, setNewSkill] = useState({
    name: "",
    category: "Submissions",
    description: "",
  });

  const filteredSkills = useMemo(
    () =>
      skillFilter === "All"
        ? allSkills
        : allSkills.filter((s) => s.category === skillFilter),
    [allSkills, skillFilter]
  );

  const noteSkillSet = useMemo(
    () => new Set(notes.map((n) => n.skill).filter(Boolean)),
    [notes]
  );

  async function addCustomSkill() {
    const trimmed = newSkill.name.trim();
    if (!trimmed) {
      toast.error("Skill name required");
      return;
    }
    if (allSkills.some((s) => s.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("That skill is already in your roadmap");
      return;
    }
    try {
      await onAddCustomSkill({
        name: trimmed,
        category: newSkill.category,
        description: newSkill.description.trim() || null,
      });
      setNewSkill({ name: "", category: newSkill.category, description: "" });
      setAddSkillOpen(false);
      toast.success("Custom skill added");
    } catch (err) {
      console.error("addCustomSkill failed", err);
      toast.error(err?.code === "permission-denied"
        ? "Firestore rules not published yet"
        : `Save failed: ${err?.message || "unknown error"}`);
    }
  }

  async function removeCustomSkill(id) {
    try {
      await onRemoveCustomSkill(id);
      toast.success("Skill removed");
    } catch (err) {
      console.error("removeCustomSkill failed", err);
      toast.error(`Remove failed: ${err?.message || "unknown error"}`);
    }
  }

  return (
    <section className="tactical-card p-6" data-testid="roadmap-section">
      <p className="font-label text-red-600">// 03</p>
      <h2 className="font-heading text-3xl mt-1 flex items-center gap-2">
        <CheckCircle2 className="w-6 h-6" strokeWidth={2.5} /> Skill Roadmap
      </h2>
      <p className="text-zinc-500 text-sm mt-1">
        Hover any skill for technique notes. Add your own to personalize.
      </p>

      {/* Belt-styled progress */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between mb-2">
          <p className="font-label">Progress</p>
          <p className="font-heading text-2xl">
            <span className="text-red-600">{progress}</span>
            <span className="text-zinc-500 text-sm ml-1">%</span>
          </p>
        </div>
        <div className="belt-bar" data-testid="progress-bar">
          <div
            className="belt-fill"
            style={{
              width: `${progress}%`,
              background: currentBelt.fill,
              borderRight: belt === "White" ? "1px solid #d4d4d8" : "none",
            }}
          />
          <div className="belt-tip" />
          <div className="belt-stripe" />
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="mt-5 flex flex-wrap gap-1.5" data-testid="skill-category-tabs">
        {ALL_CATEGORY_KEYS.map((cat) => {
          const catMeta = SKILL_CATEGORIES.find((c) => c.key === cat);
          const total =
            cat === "All"
              ? allSkills.length
              : allSkills.filter((s) => s.category === cat).length;
          const done =
            cat === "All"
              ? completedSkills
              : allSkills.filter(
                  (s) => s.category === cat && noteSkillSet.has(s.name)
                ).length;
          const active = skillFilter === cat;
          return (
            <button
              key={cat}
              onClick={() => setSkillFilter(cat)}
              data-testid={`skill-tab-${cat.toLowerCase()}`}
              className={`text-[10px] font-heading tracking-wider px-2.5 py-1.5 border transition-colors ${
                active
                  ? "border-red-600 bg-red-600 text-white"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
              style={
                !active && catMeta
                  ? { borderLeftWidth: "3px", borderLeftColor: catMeta.color }
                  : undefined
              }
            >
              {cat.toUpperCase()}{" "}
              {total > 0 && (
                <span className="opacity-70">
                  · {done}/{total}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Skill list */}
      <div
        className="mt-4 space-y-1.5 max-h-[480px] overflow-y-auto pr-1"
        data-testid="skill-list"
      >
        {filteredSkills.map((skill) => (
          <SkillRow
            key={skill.name}
            skill={skill}
            done={noteSkillSet.has(skill.name)}
            onRemove={skill.custom ? () => removeCustomSkill(skill.id) : null}
          />
        ))}
        {filteredSkills.length === 0 && (
          <div className="border border-dashed border-zinc-800 py-6 text-center">
            <p className="font-label text-zinc-600">No skills in this category yet</p>
          </div>
        )}
      </div>

      {/* Add custom skill */}
      {addSkillOpen ? (
        <div
          className="mt-4 border border-zinc-800 bg-[#0A0A0A] p-3 space-y-2"
          data-testid="add-skill-form"
        >
          <input
            placeholder="Skill name, ex: Berimbolo"
            value={newSkill.name}
            onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
            data-testid="custom-skill-name-input"
            className="w-full bg-[#141414] border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none"
            autoFocus
          />
          <textarea
            placeholder="Description (optional): how you set it up, key details..."
            value={newSkill.description}
            onChange={(e) =>
              setNewSkill({ ...newSkill, description: e.target.value })
            }
            data-testid="custom-skill-description-input"
            className="w-full bg-[#141414] border border-zinc-800 px-3 py-2 text-xs min-h-[60px] focus:border-red-600 focus:outline-none resize-y"
          />
          <div className="flex items-center gap-2">
            <select
              value={newSkill.category}
              onChange={(e) =>
                setNewSkill({ ...newSkill, category: e.target.value })
              }
              data-testid="custom-skill-category-select"
              className="flex-1 bg-[#141414] border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none"
            >
              {SKILL_CATEGORIES.map((c) => (
                <option key={c.key}>{c.key}</option>
              ))}
            </select>
            <button
              onClick={addCustomSkill}
              data-testid="save-custom-skill-button"
              className="bg-red-600 hover:bg-red-700 text-white font-heading tracking-wider px-3 py-2 text-xs flex items-center gap-1"
            >
              <Plus className="w-3 h-3" strokeWidth={3} /> ADD
            </button>
            <button
              onClick={() => {
                setAddSkillOpen(false);
                setNewSkill({ name: "", category: newSkill.category, description: "" });
              }}
              data-testid="cancel-custom-skill-button"
              className="border border-zinc-800 hover:border-zinc-600 text-zinc-400 px-2 py-2"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAddSkillOpen(true)}
          data-testid="open-add-skill"
          className="mt-4 w-full border border-dashed border-zinc-700 hover:border-red-600 hover:text-red-500 text-zinc-400 py-2.5 text-xs font-heading tracking-wider flex items-center justify-center gap-2 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Custom Skill
        </button>
      )}

      {progress === 100 && (
        <button
          onClick={onOpenPromotion}
          data-testid="open-promotion-card"
          className="mt-5 w-full bg-red-600 hover:bg-red-700 text-white font-heading tracking-wider py-3 flex items-center justify-center gap-2"
        >
          <Award className="w-4 h-4" strokeWidth={3} />
          View Promotion Card
        </button>
      )}
    </section>
  );
}

function SkillRow({ skill, done, onRemove }) {
  const catMeta = SKILL_CATEGORIES.find((c) => c.key === skill.category);
  const slug = skill.name.toLowerCase().replace(/\s+/g, "-");
  const hasTooltip = Boolean(skill.description);

  const row = (
    <div
      data-testid={`skill-${slug}`}
      className={`group flex items-center justify-between border-l-2 px-3 py-2 ${
        done ? "bg-[#0A0A0A]" : ""
      } ${hasTooltip ? "cursor-help" : ""}`}
      style={{ borderLeftColor: done ? "#DC2626" : catMeta?.color || "#27272A" }}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span
          className={`text-sm ${
            done ? "text-white font-semibold" : "text-zinc-400"
          } truncate`}
        >
          {skill.name}
        </span>
        {hasTooltip && (
          <Info className="w-3 h-3 text-zinc-600 flex-shrink-0" strokeWidth={2.5} />
        )}
        {skill.custom && (
          <span className="font-mono text-[9px] text-zinc-600 border border-zinc-800 px-1.5 py-0.5">
            CUSTOM
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {done ? (
          <CheckCircle2 className="w-4 h-4 text-red-600" strokeWidth={2.5} />
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-700" />
        )}
        {onRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            data-testid={`remove-custom-skill-${slug}`}
            className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-500 ml-1 transition-opacity"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  if (!hasTooltip) return row;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent
        side="left"
        align="start"
        className="max-w-xs bg-[#141414] border border-red-600 text-white rounded-none px-3 py-2"
      >
        <p className="font-heading text-xs tracking-wider text-red-500 mb-1">
          {skill.category.toUpperCase()}
        </p>
        <p className="text-xs leading-relaxed text-zinc-200">{skill.description}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// (Derived state helper removed — Dashboard now owns the source of truth.)
